import React from 'react';

import { useTranslation } from '@modernloop/i18n';
import { assertIsoTimestamp, formatToTimeZone, getLocalTimezone } from '@modernloop/shared/datetime';
import { Chip, Stack, Typography } from '@mui/material';

import { PushScheduleToAtsDryRunQuery } from 'src/generated/mloop-graphql';

export type InterviewResult = NonNullable<PushScheduleToAtsDryRunQuery['pushScheduleToAtsDryRun']>['results'][number];

type UpdateDetailsProps = {
  updateDetails: InterviewResult['updateDetails'];
};

/**
 * Displays the details of what will be updated for an interview when pushing to ATS.
 * Shows time changes, scorecard changes, and interviewer changes with visual diff.
 */
const PushScheduleToAtsUpdateDetails: React.FC<UpdateDetailsProps> = ({ updateDetails }) => {
  const { t } = useTranslation('hiring-dashboard-schedule');

  if (!updateDetails) {
    return null;
  }

  const { timeChanged, scorecardChanged, addedInterviewers, removedInterviewers } = updateDetails;

  const hasInterviewerChanges =
    (addedInterviewers && addedInterviewers.length > 0) || (removedInterviewers && removedInterviewers.length > 0);

  if (!timeChanged && !scorecardChanged && !hasInterviewerChanges) {
    return null;
  }

  const formatTime = (timestamp: string | null | undefined): string => {
    if (!timestamp) {
      return '—';
    }
    return formatToTimeZone(assertIsoTimestamp(timestamp), 'MMM d, h:mm a', getLocalTimezone());
  };

  return (
    <Stack spacing={1} pl={2} pt={0.5}>
      {timeChanged && (
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            {t('schedule.pushToAts.updateDetails.timeChange')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" pl={2}>
            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
              {formatTime(updateDetails.previousStartAt)} - {formatTime(updateDetails.previousEndAt)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              →
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {formatTime(updateDetails.newStartAt)} - {formatTime(updateDetails.newEndAt)}
            </Typography>
          </Stack>
        </Stack>
      )}
      {scorecardChanged && (
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            {t('schedule.pushToAts.updateDetails.scorecardChange')}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" pl={2}>
            <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
              {updateDetails.previousAtsInterviewDefinition?.name || '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              →
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {updateDetails.newAtsInterviewDefinition?.name || '—'}
            </Typography>
          </Stack>
        </Stack>
      )}
      {hasInterviewerChanges && (
        <Stack spacing={0.5}>
          <Typography variant="body2" color="text.secondary">
            {t('schedule.pushToAts.updateDetails.interviewerChanges')}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" pl={2}>
            {removedInterviewers?.map((interviewer) => (
              <Chip
                key={interviewer.id}
                label={interviewer.fullName || t('schedule.pushToAts.unknownInterviewer')}
                size="small"
                color="error"
                variant="outlined"
                sx={{ textDecoration: 'line-through' }}
              />
            ))}
            {addedInterviewers?.map((interviewer) => (
              <Chip
                key={interviewer.id}
                label={interviewer.fullName || t('schedule.pushToAts.unknownInterviewer')}
                size="small"
                color="success"
                variant="outlined"
              />
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default PushScheduleToAtsUpdateDetails;
